import type { UserAssetV2Record } from "@apis/http/api";
import type { FocusType } from "../librarySlice";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseImageBatchSelectionReturn {
  selectedIds: Set<number>;
  selectedCount: number;
  selectionActive: boolean;
  isSelected: (id: number) => boolean;
  toggle: (id: number) => void;
  setSelected: (id: number, selected: boolean) => void;
  clear: () => void;
}

export const useImageBatchSelection = (
  imageItems: UserAssetV2Record[],
  focusType: FocusType,
): UseImageBatchSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (focusType !== "image") {
      setSelectedIds(new Set());
    }
  }, [focusType]);

  useEffect(() => {
    const validIds = new Set(imageItems.map((item) => item.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [imageItems]);

  const toggle = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setSelected = useCallback((id: number, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id: number) => selectedIds.has(id), [selectedIds]);

  const selectedCount = selectedIds.size;
  const selectionActive = selectedCount > 0;

  return useMemo(
    () => ({
      selectedIds,
      selectedCount,
      selectionActive,
      isSelected,
      toggle,
      setSelected,
      clear,
    }),
    [selectedIds, selectedCount, selectionActive, isSelected, toggle, setSelected, clear],
  );
};
